import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthSession } from "../../auth/keycloak.js";
import { facilityApi } from "../../api/client.js";

/** A failed facility mutation — carries the HTTP status + the contract's machine `code` for friendly copy. */
export interface FacilityMutationError extends Error {
  status: number;
  code?: string;
  /** The raw response body — preserved so the shared error dialog can render structured detail. */
  body?: unknown;
}

function fail(status: number, body: unknown): FacilityMutationError {
  const err = new Error(`facility mutation failed (${status})`) as FacilityMutationError;
  err.status = status;
  err.code = (body as { code?: string } | undefined)?.code;
  err.body = body;
  return err;
}

export interface CreateFacilityVars {
  name: string;
  parentId: number | null;
  categoryIds: number[];
}

/**
 * Create a facility (ADR-006 §5). Like `useMoveFacility`: the shared ts-rest transport never throws, so the mutationFn
 * throws a typed `{status, code}` error on any non-200 (so `onError` fires); `onSuccess` invalidates the facilities
 * query (v5 filters-object prefix) so the tree refetches with the new node.
 */
export function useCreateFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateFacilityVars) => {
      const res = await facilityApi.createFacility({ body: vars });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
      // A create that ATTACHES categories (inherit-from-parent) bumps each category's `facilityCount` — invalidate the
      // categories query too, exactly as `useAttachCategory` does, so Manage doesn't show stale counts (panel).
      if (variables.categoryIds.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ["categories", session?.realm, session?.sub] });
      }
    },
  });
}

export interface RenameFacilityVars {
  id: number;
  name: string;
}

/**
 * Rename a facility (ADR-006 §1; `PATCH /facilities/:id`; authz: manage). Same transport contract as the other
 * facility mutations — the shared ts-rest transport never throws, so a non-200 becomes a typed `{status, code}` error
 * (so `onError` / the shared error dialog fire). `onSuccess` invalidates the facilities query prefix so the tree
 * refetches with the new name. Not a topology change (no advisory lock server-side).
 */
export function useRenameFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: RenameFacilityVars) => {
      const res = await facilityApi.updateFacility({ params: { id }, body: { name } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
    },
  });
}

/**
 * Archive a facility + its whole subtree (ADR-006 §2; `POST /facilities/:id/archive`; authz: manage on the FULL
 * subtree). Same transport contract as the other facility mutations — a non-200 becomes a typed `{status, code}` error.
 * `onSuccess` invalidates the facilities query so the tree refetches (the archived subtree leaves All/Favorites).
 */
export function useArchiveFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await facilityApi.archiveFacility({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.facilities;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
    },
  });
}

/**
 * Unarchive a facility + its whole subtree (`POST /facilities/:id/unarchive`; authz: manage on the FULL subtree). The
 * server guards it: `FACILITY_NOT_ARCHIVED` (target isn't archived) and `FACILITY_ANCESTOR_ARCHIVED` (a strict
 * ancestor is still archived) both surface as a typed 409 → the shared error dialog. `onSuccess` refetches the tree
 * (the restored subtree returns to All).
 */
export function useUnarchiveFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await facilityApi.unarchiveFacility({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.facilities;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
    },
  });
}

/**
 * The pre-delete "what's connected" stats (ADR-006 §3; `GET /facilities/:id/stats`; authz: manage on the FULL
 * subtree). Fetched when the delete dialog opens (`id != null`). **`staleTime: 0` + `gcTime: 0`** so a reopen never
 * serves a cached, stale `expected` token — the dialog must confirm against the CURRENT subtree, and the token is
 * re-checked server-side under the delete lock (panel). `retry: false` so a 403/404 surfaces immediately (not after
 * retries) for the dialog to branch on. A non-200 throws the typed `{status, code}` error → `query.error`.
 */
export function useFacilityStats(session: AuthSession | null, id: number | null) {
  return useQuery({
    queryKey: ["facility-stats", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: async () => {
      const res = await facilityApi.facilityStats({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
  });
}

/**
 * Deep-copy a facility subtree as a sibling (ADR-008, RED ZONE; `POST /facilities/:id/duplicate`; authz: manage on the
 * FULL subtree + create on the parent). A non-200 throws the typed `{status, code}` error → the shared error dialog
 * (403 forbidden / 404 gone / 409 archived source). `onSuccess` invalidates the facilities query so the tree refetches
 * with the new "<name> Copy" sibling.
 */
export function useDuplicateFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await facilityApi.duplicateFacility({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.facilities;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
    },
  });
}

export interface DeleteFacilityVars {
  id: number;
  /** The opaque snapshot from `useFacilityStats` — the server re-checks it under the lock and 409s if the subtree
   *  changed (archive / rename / membership / categories) since the confirmation. */
  expected: string;
}

/**
 * Hard-delete a facility subtree (ADR-006 §4, RED ZONE; `DELETE /facilities/:id?expected=…`; authz: manage on the FULL
 * subtree). A non-200 throws the typed `{status, code}` error — the dialog branches on **409 `FACILITY_STATS_STALE`**
 * (stale confirmation → refresh) vs other errors. `onSuccess` invalidates the facilities query so the tree refetches
 * without the deleted subtree.
 */
export function useDeleteFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expected }: DeleteFacilityVars) => {
      const res = await facilityApi.deleteFacility({ params: { id }, query: { expected } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // { deleted: number }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
    },
  });
}
